import { getEmailSchema, getPasswordSchema } from '@diagon-alley/common';
import { useTranslate } from '@hooks/i18n';
import {
    Anchor,
    Button,
    Card,
    PasswordInput,
    PinInput,
    Radio,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    ALL_ORGANIZATIONS,
    completePasswordReset,
    startPasswordReset,
    verifyPasswordResetOtp,
} from '@api/passwordReset';
import { useDebouncedBusy } from '../LoginLanding';

type Step = 'EMAIL' | 'OTP' | 'RESET';

export const ForgotPasswordPage: FC = () => {
    const { t } = useTranslate();
    const nav = useNavigate();
    const [step, setStep] = useState<Step>('EMAIL');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    /**
     * Issued by the verify step and required by the reset step. It is the only
     * proof the server has that this browser actually received the code.
     */
    const [resetToken, setResetToken] = useState<string | null>(null);
    /** Only set when the address owns logins in several orgs — the reset changes one. */
    const [organizations, setOrganizations] = useState<
        Array<{ id: string; name: string }> | null
    >(null);
    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [msg, setMsg] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [timer, setTimer] = useState(0);
    const { busy, start, stop } = useDebouncedBusy(400);

    // resend countdown
    useEffect(() => {
        if (timer <= 0) return;
        const id = window.setTimeout(() => setTimer((v) => v - 1), 1000);
        return () => window.clearTimeout(id);
    }, [timer]);

    const sendOtp = useCallback(async () => {
        setErr(null);
        setMsg(null);
        const ok = getEmailSchema().safeParse(email);
        if (!ok.success)
            return setErr(
                ok.error.issues[0]?.message || t('forgot.errors.email'),
            );
        start();
        const res = await startPasswordReset(email);
        stop();
        if (!res.ok) return setErr(res.error || t('forgot.errors.generic'));
        setMsg(t('forgot.otpSent'));
        setStep('OTP');
        setTimer(30);
    }, [email, start, stop, t]);

    const verify = useCallback(async () => {
        setErr(null);
        setMsg(null);
        if (!/^\d{6}$/.test(otp)) return setErr(t('forgot.errors.otpFormat'));
        start();
        const res = await verifyPasswordResetOtp(email, otp);
        stop();
        if (!res.ok || !res.data)
            return setErr(res.error || t('forgot.errors.otpInvalid'));
        setResetToken(res.data.resetToken);
        const choices = res.data.organizations ?? null;
        setOrganizations(choices);
        setOrganizationId(choices?.[0]?.id ?? null);
        setMsg(t('forgot.otpVerified'));
        setStep('RESET');
    }, [email, otp, start, stop, t]);

    const reset = useCallback(async () => {
        setErr(null);
        setMsg(null);
        const pass = getPasswordSchema().safeParse(password);
        if (!pass.success) {
            // The shared schema returns sentence fragments ("must be at least 8
            // characters long"), so give them a subject before showing them.
            const detail = pass.error.issues[0]?.message;
            return setErr(
                detail
                    ? t('forgot.errors.weakDetail', { detail })
                    : t('forgot.errors.weak'),
            );
        }
        if (password !== confirm) return setErr(t('forgot.errors.mismatch'));
        if (!resetToken) return setErr(t('forgot.errors.generic'));
        if (organizations && !organizationId)
            return setErr('Choose which organisation to reset');
        start();
        const res = await completePasswordReset(
            resetToken,
            password,
            organizationId ?? undefined,
        );
        stop();
        if (!res.ok) return setErr(res.error || t('forgot.errors.generic'));
        setMsg(t('forgot.resetSuccess'));
        setTimeout(() => nav('/login'), 1200);
    }, [resetToken, organizations, organizationId, password, confirm, start, stop, nav, t]);

    const resend = useCallback(async () => {
        if (timer > 0) return;
        await sendOtp();
    }, [timer, sendOtp]);

    return (
        <Stack
            align="center"
            justify="center"
            p="xl"
            className="min-h-screen w-full"
        >
            <Card withBorder radius="xl" p="lg" className="w-[380px]">
                <Stack>
                    {msg && (
                        <div className="rounded-md border border-green-200 bg-green-50 p-2 text-sm text-green-700">
                            {msg}
                        </div>
                    )}
                    {err && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                            {err}
                        </div>
                    )}

                    {step === 'EMAIL' && (
                        <Stack gap="sm">
                            <Text size="sm" fw={600}>
                                {t('forgot.title')}
                            </Text>
                            <TextInput
                                label={t('forgot.emailLabel')}
                                placeholder={t('forgot.emailPlaceholder')}
                                value={email}
                                onChange={(e) =>
                                    setEmail(e.currentTarget.value)
                                }
                                autoComplete="email"
                            />
                            <Button
                                radius="xl"
                                onClick={sendOtp}
                                loading={busy}
                                style={{
                                    backgroundColor: '#CBC3E3',
                                    color: 'black',
                                }}
                            >
                                {t('forgot.sendOtp')}
                            </Button>
                        </Stack>
                    )}

                    {step === 'OTP' && (
                        <Stack gap="sm">
                            <Text size="sm">
                                {t('forgot.enterOtp', { email })}
                            </Text>
                            <PinInput
                                length={6}
                                oneTimeCode
                                value={otp}
                                onChange={setOtp}
                            />
                            <Stack gap={2}>
                                <Button
                                    radius="xl"
                                    onClick={verify}
                                    loading={busy}
                                    style={{
                                        backgroundColor: '#CBC3E3',
                                        color: 'black',
                                    }}
                                >
                                    {t('forgot.verify')}
                                </Button>
                                <Button
                                    variant="subtle"
                                    onClick={resend}
                                    disabled={timer > 0}
                                    color="black"
                                >
                                    {timer > 0
                                        ? t('forgot.resendIn', { s: timer })
                                        : t('forgot.resend')}
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    {step === 'RESET' && (
                        <Stack gap="sm">
                            {organizations && (
                                <Radio.Group
                                    label="This email has logins in more than one organisation"
                                    description="Choose the one whose password to change"
                                    value={organizationId ?? ''}
                                    onChange={setOrganizationId}
                                >
                                    <Stack gap="xs" mt="xs">
                                        {organizations.map((org) => (
                                            <Radio
                                                key={org.id}
                                                value={org.id}
                                                label={org.name}
                                            />
                                        ))}
                                        <Radio
                                            value={ALL_ORGANIZATIONS}
                                            label="All of them — use the new password everywhere"
                                        />
                                    </Stack>
                                </Radio.Group>
                            )}
                            <PasswordInput
                                label={t('forgot.newPassword')}
                                value={password}
                                onChange={(e) =>
                                    setPassword(e.currentTarget.value)
                                }
                            />
                            <PasswordInput
                                label={t('forgot.confirmPassword')}
                                value={confirm}
                                onChange={(e) =>
                                    setConfirm(e.currentTarget.value)
                                }
                            />
                            <Button
                                radius="xl"
                                onClick={reset}
                                loading={busy}
                                style={{
                                    backgroundColor: '#CBC3E3',
                                    color: 'black',
                                }}
                            >
                                {t('forgot.reset')}
                            </Button>
                        </Stack>
                    )}

                    <Anchor
                        onClick={() => nav('/login')}
                        size="sm"
                        className="cursor-pointer"
                        c="black"
                    >
                        {t('forgot.backToLogin')}
                    </Anchor>
                </Stack>
            </Card>
        </Stack>
    );
};
